import moment from 'moment';
import { Currency } from '../../../index.js';
import { CaseVoucherSource, InterestPeriodDefaultBehavior, interestPeriodWindowsOverlap, } from '../../case-vouchers/index.js';
import { Interval, PaymentPlanEntryStatus, PaymentPlanEntryTypes, isPreservedEntryStatus, resolvePaymentPlansConfig, } from '../../payment-plans/index.js';
import { VoucherCategory, VoucherTypeIds, VoucherTypeSetting, } from '../../vouchers/index.js';
import { CaseVoucherInterestSettings } from '../../vouchers/setup.js';
import { lastDanishBankDayOfMonth } from '../dates/danish-bank-days.js';
import { dayIn, startOf } from '../engine-v2/dates.js';
import helpers from '../index.js';
import { isEngineV2, getEconomyPolicy, } from '../policy.js';
import { parseDate } from '../utils.js';
import { generateByAmount } from './methods/amount.js';
import { generateByDuration } from './methods/duration.js';
import { generateSettlement } from './methods/settlement.js';
export const hasValidFeeAmount = (amount) => typeof amount === 'number' && amount > 0;
export const appendPerEntryFeeVoucher = (vouchers, feeStructure, date) => {
    if (!hasValidFeeAmount(feeStructure?.perEntryFee?.amount)) {
        return vouchers;
    }
    return [
        ...vouchers,
        mockCostCaseVoucher({
            amount: -feeStructure.perEntryFee.amount,
            voucherTypeId: feeStructure.perEntryFee.voucherTypeId,
            source: CaseVoucherSource.Collector,
            date,
        }),
    ];
};
export const getEffectiveFeeStructure = (creditorConfig, tenantConfig) => {
    const { defaultFeeStructure } = resolvePaymentPlansConfig(creditorConfig, tenantConfig);
    if (!defaultFeeStructure) {
        return undefined;
    }
    const feeStructure = {};
    if (defaultFeeStructure.initialFee &&
        defaultFeeStructure.initialFee.amount > 0) {
        feeStructure.initialFee = {
            amount: defaultFeeStructure.initialFee.amount,
            voucherTypeId: defaultFeeStructure.initialFee.voucherTypeId,
        };
    }
    if (defaultFeeStructure.perEntryFee &&
        defaultFeeStructure.perEntryFee.amount > 0) {
        feeStructure.perEntryFee = {
            amount: defaultFeeStructure.perEntryFee.amount,
            voucherTypeId: defaultFeeStructure.perEntryFee.voucherTypeId,
        };
    }
    return Object.keys(feeStructure).length > 0 ? feeStructure : undefined;
};
export const gen = async (tenant, creditor, economyEngine, caseVouchers, documents, fixedDistributions, config, conditions, options) => {
    const start = performance.now();
    const economyPolicy = getEconomyPolicy(tenant, creditor, economyEngine);
    const { reminderDays: reminderTimespan, deadlineDays: paymentDeadlineTimespan, cancelDays: cancelTimespan, } = resolvePaymentPlansConfig(creditor.paymentPlansConfig, tenant.paymentPlansConfig);
    // `config.plan` carries the seed entries. Recalculation seeds settled
    // entries with their status, so the balance projection doesn't count them
    // as future deposits on top of their payment vouchers; everything else
    // defaults to PENDING.
    let plan = (config.plan ?? []).map((entry) => ({
        ...entry,
        status: entry.status ?? PaymentPlanEntryStatus.PENDING,
        interestAmount: null,
    }));
    const startDate = moment(config.startDate);
    let filteredVouchers = helpers.vouchers.postProcessing(caseVouchers, {
        includeDeletedVouchers: false,
        includeExpiredVouchers: false,
        includeIrrecoverableVouchers: false,
    });
    if (config.interestFree) {
        filteredVouchers = interestFreePeriod(filteredVouchers, config.interestFree);
    }
    // Preserved seeds are history, so they leave the start date to the config.
    const lastEntry = plan.findLast((entry) => !isPreservedEntryStatus(entry.status));
    let currDate = lastEntry &&
        addInterval(config.interval, moment(lastEntry.date)).isAfter(moment().startOf('day'))
        ? addInterval(config.interval, moment(lastEntry.date))
        : snapToBankDay(startDate.clone(), config.interval);
    if (currDate.isBefore(moment().startOf('day'))) {
        throw new Error(`Start date can't be in the past`);
    }
    if (config.amountUpfront && !plan.length) {
        if (typeof config.amountUpfront !== 'number') {
            throw new Error(`Please provide a number, not a ${typeof config.amountUpfront}`);
        }
        if (config.amountUpfront <= 0) {
            throw new Error(`Up front amount cannot be less or equals 0`);
        }
        // moment(null) is an invalid date, so fall back to the plan start
        const upfrontDate = moment(config.dateUpfront ?? config.startDate);
        let perEntryFee = 0;
        // Add per-entry fee for upfront entry
        const perEntryFeeAmount = config.feeStructure?.perEntryFee?.amount;
        if (hasValidFeeAmount(perEntryFeeAmount)) {
            filteredVouchers = appendPerEntryFeeVoucher(filteredVouchers, config.feeStructure, upfrontDate.toISOString());
            if (!options.recalculation) {
                perEntryFee = perEntryFeeAmount;
            }
        }
        const newEntry = {
            reminder: upfrontDate.clone().format(),
            date: upfrontDate.add(reminderTimespan, 'days').clone().format(),
            deadline: upfrontDate
                .add(paymentDeadlineTimespan, 'days')
                .clone()
                .format(),
            cancel: upfrontDate.add(cancelTimespan, 'days').clone().format(),
            type: PaymentPlanEntryTypes.UPFRONT,
            amount: config.amountUpfront + perEntryFee,
            interestAmount: null,
            key: crypto.randomUUID(),
            status: PaymentPlanEntryStatus.PENDING,
        };
        plan = plan.concat([newEntry]);
    }
    if (!options.recalculation &&
        hasValidFeeAmount(config.feeStructure?.initialFee?.amount)) {
        filteredVouchers = [
            ...filteredVouchers,
            mockCostCaseVoucher({
                amount: -config.feeStructure.initialFee.amount,
                voucherTypeId: config.feeStructure.initialFee.voucherTypeId,
                source: CaseVoucherSource.Collector,
                date: currDate.toISOString(),
            }),
        ];
    }
    if (config.settlementTotal != null) {
        // Settlement (accord) is its own generation mode: the config's method
        // only decides how the agreed sum is cut into entries.
        const result = await generateSettlement({
            plan,
            filteredVouchers,
            documents,
            economyPolicy,
            fixedDistributions,
            currDate,
            reminderTimespan,
            paymentDeadlineTimespan,
            cancelTimespan,
            recalculation: !!options.recalculation,
            conditions,
        }, config, config.settlementTotal);
        plan = result.plan;
        filteredVouchers = result.filteredVouchers;
    }
    else if (config.method === 'duration' && config.duration) {
        const result = await generateByDuration({
            duration: config.duration,
            interval: config.interval,
            feeStructure: config.feeStructure,
            plan,
            filteredVouchers,
            documents,
            economyPolicy,
            fixedDistributions,
            currDate,
            reminderTimespan,
            paymentDeadlineTimespan,
            cancelTimespan,
            conditions,
        });
        plan = result.plan;
        filteredVouchers = result.filteredVouchers;
    }
    else if (config.method === 'amount' && config.amount) {
        const result = await generateByAmount({
            amount: config.amount,
            scheduledAmountChanges: config.scheduledAmountChanges,
            interval: config.interval,
            feeStructure: config.feeStructure,
            plan,
            filteredVouchers,
            documents,
            economyPolicy,
            fixedDistributions,
            currDate,
            reminderTimespan,
            paymentDeadlineTimespan,
            cancelTimespan,
            recalculation: !!options.recalculation,
            conditions,
        });
        plan = result.plan;
        filteredVouchers = result.filteredVouchers;
    }
    else {
        throw new Error('Please select either an amount or a duration');
    }
    const final = options.projectInterest === true
        ? await addInterestAmounts(filteredVouchers, documents, economyPolicy, fixedDistributions, plan, conditions)
        : plan;
    let end = performance.now();
    if (end - start > 10) {
        console.log('Payment plan gen time:', end - start);
    }
    return final;
};
/* New entries for a plan replacing paymentPlan (edit / skip-to). The server
   keeps the preserved entries, so a settlement plan seeds them: they count
   toward the agreed sum, and AWAITING ones as deposits in the debt
   projection. Plain plans aren't seeded, since their generators resize seed
   entries. */
export const genReplacement = async (tenant, creditor, economyEngine, caseVouchers, documents, fixedDistributions, paymentPlan, config, conditions) => {
    const preservedEntries = paymentPlan.plan
        .filter((entry) => isPreservedEntryStatus(entry.status))
        .map((entry) => ({ ...entry, amount: Math.abs(entry.amount) }));
    const entries = await gen(tenant, creditor, economyEngine, caseVouchers, documents, fixedDistributions, config.settlementTotal != null
        ? { ...config, plan: preservedEntries }
        : config, conditions, { recalculation: true });
    return entries.filter((entry) => !isPreservedEntryStatus(entry.status));
};
export const createNewNormalEntry = (currDate, reminderTimespan, paymentDeadlineTimespan, cancelTimespan, amount) => {
    const cD = currDate.clone();
    return {
        reminder: cD.subtract(reminderTimespan, 'days').clone().format(),
        date: cD.add(reminderTimespan, 'days').clone().format(),
        deadline: cD.add(paymentDeadlineTimespan, 'days').clone().format(),
        cancel: cD.add(cancelTimespan, 'days').clone().format(),
        type: PaymentPlanEntryTypes.NORMAL,
        amount,
        interestAmount: null,
        key: crypto.randomUUID(),
        status: PaymentPlanEntryStatus.PENDING,
    };
};
// Snap a date to a Danish bank day at 06:00 Copenhagen — but only for the
// LASTBANKDAYMONTH interval, which is the explicit "Danish payday" mode.
// All other intervals keep the user-picked date unchanged so existing
// behaviour (and existing plans that already have non-bank-day entries)
// is preserved.
export const snapToBankDay = (date, interval) => {
    if (interval === Interval.LASTBANKDAYMONTH) {
        return lastDanishBankDayOfMonth(date);
    }
    return date.clone();
};
export const addInterval = (interval, currDate) => {
    const next = currDate.clone();
    switch (interval) {
        case Interval['TWO-WEEKS']:
            next.add(2, 'weeks');
            return next;
        case Interval.WEEK:
            next.add(1, 'week');
            return next;
        case Interval.MONTH:
            next.add(1, 'month');
            return next;
        case Interval['TWO-MONTHS']:
            next.add(2, 'months');
            return next;
        case Interval.QUARTERLY:
            next.add(3, 'months');
            return next;
        case Interval.YEARLY:
            next.add(1, 'year');
            return next;
        case Interval.LASTBANKDAYMONTH:
            next.add(1, 'month');
            return lastDanishBankDayOfMonth(next);
    }
};
// Entries the algorithm fills in: REST entries when present (amount mode),
// otherwise all NORMAL entries (duration mode). UPFRONT entries are always
// kept as-is since their amount comes directly from config.
const isFillableEntry = (entry, planHasRest) => {
    if (planHasRest) {
        return entry.type === PaymentPlanEntryTypes.REST;
    }
    return entry.type === PaymentPlanEntryTypes.NORMAL;
};
export const calculateAmounts = async (caseVouchers, documents, economyPolicy, fixedDistributions, plan, conditions) => {
    const planHasRest = plan.some((e) => e.type === PaymentPlanEntryTypes.REST);
    for (const entry of plan) {
        if (isFillableEntry(entry, planHasRest)) {
            entry.amount = 0;
        }
    }
    const upperBound = await getEndAmountWithOverpayment(caseVouchers, documents, economyPolicy, fixedDistributions, plan, conditions);
    let sum = upperBound;
    let fillableCount = 0;
    for (let entry of plan) {
        if (isFillableEntry(entry, planHasRest)) {
            fillableCount += 1;
        }
        else {
            sum -=
                entry.status !== PaymentPlanEntryStatus.SETTLED ? entry.amount || 0 : 0;
        }
    }
    const amountWithOverpaymentsPrEntry = Math.ceil(sum / fillableCount);
    const newPlan = plan.map((p) => ({
        ...p,
        amount: isFillableEntry(p, planHasRest)
            ? amountWithOverpaymentsPrEntry
            : p.amount,
    }));
    return (await findEntryAmount(caseVouchers, documents, economyPolicy, newPlan, amountWithOverpaymentsPrEntry, upperBound, fixedDistributions, conditions)).map((entry) => ({
        ...entry,
        interestAmount: null,
    }));
};
const getEndAmountWithOverpayment = async (caseVouchers, documents, economyPolicy, fixedDistributions, plan, conditions) => {
    const latestDate = getLatestDate(plan);
    const vs = await helpers.interest.calculate(caseVouchers, documents, economyPolicy, fixedDistributions, latestDate, {
        c: { preventInterestExpiration: true },
        freezeLimitation: true,
        ...conditions,
    });
    return -Math.round(helpers.overview.balance(vs));
};
const getLatestDate = (plan) => {
    const latestEntry = plan[plan.length - 1];
    if (!latestEntry) {
        throw new Error('No dates found');
    }
    return typeof latestEntry.date === 'string'
        ? latestEntry.date
        : moment(latestEntry.date).format(); // TODO: Same thing?
};
const findEntryAmount = async (caseVouchers, documents, economyPolicy, newPlan, maxAmount, maxBalance, fixedDistributions, conditions) => {
    const planHasRest = newPlan.some((e) => e.type === PaymentPlanEntryTypes.REST);
    let lower = 0;
    let upper = maxAmount;
    for (const entry of newPlan) {
        if (isFillableEntry(entry, planHasRest)) {
            entry.amount = lower;
        }
    }
    let lowerBalance = await balanceAfterPlan(caseVouchers, documents, economyPolicy, fixedDistributions, newPlan, conditions);
    for (const entry of newPlan) {
        if (isFillableEntry(entry, planHasRest)) {
            entry.amount = upper;
        }
    }
    let upperBalance = await balanceAfterPlan(caseVouchers, documents, economyPolicy, fixedDistributions, newPlan, conditions);
    let i = 0;
    while (true) {
        const dx = Math.round(((upper - lower) * 1) / 2);
        let amount = lower + dx;
        const interpolatedAmount = Math.round(upper - (upperBalance * (upper - lower)) / (upperBalance - lowerBalance));
        if (interpolatedAmount > lower &&
            interpolatedAmount < upper &&
            i % 3 !== 1) {
            amount = interpolatedAmount;
        }
        if ((interpolatedAmount === upper || interpolatedAmount === lower) &&
            upper - lower > 3 &&
            i % 3 !== 1) {
            const nudgedAmount = interpolatedAmount === upper ? upper - 1 : lower + 1;
            amount = nudgedAmount;
        }
        if (lower === upper) {
            break;
        }
        if (amount === upper && lower + 1 === upper) {
            lower += 1;
        }
        for (const entry of newPlan) {
            if (isFillableEntry(entry, planHasRest)) {
                entry.amount = amount;
            }
        }
        const balance = await balanceAfterPlan(caseVouchers, documents, economyPolicy, fixedDistributions, newPlan, conditions);
        if (balance == 0) {
            break;
        }
        if (balance > 0) {
            upper = amount;
            upperBalance = balance;
        }
        else {
            lower = amount;
            lowerBalance = balance;
        }
        i++;
        if (i === 100) {
            throw new Error('Finding payment plan amount took too many attempts');
        }
    }
    return newPlan;
};
export const balanceAfterPlan = async (caseVouchers, documents, economyPolicy, fixedDistributions, plan, conditions, targetDate) => {
    const projectionDate = targetDate ?? getLatestDate(plan);
    const futureVouchers = await helpers.interest.calculate(caseVouchers.concat(plan.map((p) => genFakeDeposit(p.status !== PaymentPlanEntryStatus.SETTLED
        ? parseInt(`${p.amount || 0}`)
        : 0, moment(p.date).format()))), documents, economyPolicy, fixedDistributions, projectionDate, { ...conditions, freezeLimitation: true });
    return helpers.overview.balance(futureVouchers);
};
export const getAmount = {
    upfront: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.UPFRONT);
        return entry && entry.amount;
    },
    entry: (plan) => {
        const entry = JSON.parse(JSON.stringify(plan))
            .reverse()
            .find((p) => p.type === PaymentPlanEntryTypes.NORMAL);
        return entry && entry.amount;
    },
    total: (plan) => plan.reduce((acc, entry) => acc + (entry.amount || 0), 0),
    rest: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.REST);
        return entry && entry.amount;
    },
};
export const suggestedPlans = async (caseVouchers, documents, tenant, creditor, economyEngine, fixedDistributions, conditions) => {
    const plans = resolvePaymentPlansConfig(creditor.paymentPlansConfig, tenant.paymentPlansConfig);
    const feeStructure = getEffectiveFeeStructure(creditor.paymentPlansConfig, tenant.paymentPlansConfig);
    const startDate = moment()
        .add(1, 'months')
        .startOf('month')
        .add(moment().format('HH:mm:ss'));
    const filteredVouchers = helpers.vouchers.postProcessing(caseVouchers, {
        includeDeletedVouchers: false,
        includeExpiredVouchers: false,
        includeIrrecoverableVouchers: false,
    });
    const suggestions = await Promise.all(Object.values(plans.plans).map(async (plan) => {
        return {
            plan: await gen(tenant, creditor, economyEngine, JSON.parse(JSON.stringify(filteredVouchers)), documents, fixedDistributions, {
                method: 'duration',
                startDate: startDate.format(),
                interval: Interval.MONTH,
                duration: plan.duration,
                feeStructure,
            }, conditions, {}),
            ...plan,
        };
    }));
    const sugg = suggestions
        .map((plan) => ({
        monthlyAmount: getAmount.entry(plan.plan),
        months: plan.plan.length,
        from: plan.plan[0] && plan.plan[0].date,
        to: plan.plan[plan.plan.length - 1] && plan.plan[plan.plan.length - 1].date,
        total: getAmount.total(plan.plan),
        id: plan.id,
    }))
        .filter((p) => (p.monthlyAmount || 0) >= plans.minEntryAmount);
    return sugg;
};
const YEAR = 31536000000;
export const yearlyCost = async (startDate, vouchers, documents, economyPolicy, fixedDistributions, plan, initialCosts, conditions) => {
    const startDateMS = new Date(startDate).getTime();
    const years = plan.reduce((acc, entry) => {
        const year = Math.ceil((new Date(entry.date).getTime() - startDateMS) / YEAR);
        const y = year > 0 ? year : 1;
        acc[`${y}`] = {
            year: y,
            from: startDateMS + YEAR * (y - 1),
            to: startDateMS + YEAR * y,
            entries: ((acc[`${y}`] && acc[`${y}`].entries) || []).concat(entry),
        };
        return acc;
    }, {});
    const ys = Object.values(years).sort((a, b) => a.year - b.year);
    const calculatedYears = [];
    let previousDistributedVouchers = await helpers.interest.calculate(JSON.parse(JSON.stringify(vouchers)).filter((voucher) => !initialCosts
        .map((v) => v.caseVoucherId)
        .includes(voucher.caseVoucherId)), documents, economyPolicy, fixedDistributions, startDate, { ...conditions, freezeLimitation: true });
    for (let i = 0; i < ys.length; i++) {
        const year = ys[i];
        const costs = i > 0 ? [] : initialCosts;
        const nextDistributedVouchers = await helpers.interest.calculate(JSON.parse(JSON.stringify(vouchers)), documents, economyPolicy, fixedDistributions, moment(year.to).format(), { ...conditions, freezeLimitation: true });
        const previousInterest = -helpers.overview.balanceCategory(previousDistributedVouchers, VoucherCategory.INTEREST);
        const nextInterest = -helpers.overview.balanceCategory(nextDistributedVouchers, VoucherCategory.INTEREST);
        const previousPrincipal = -helpers.overview.balanceCategory(previousDistributedVouchers, VoucherCategory.PRINCIPAL);
        const costsTotal = -helpers.overview.balance(costs);
        calculatedYears.push({
            costs,
            costFraction: (nextInterest - previousInterest + costsTotal) / previousPrincipal,
            periodFrom: year.from,
            periodTo: year.to,
        });
        previousDistributedVouchers = nextDistributedVouchers;
    }
    return calculatedYears;
};
export const mockCostCaseVoucher = (partial) => ({
    ...partial,
    category: VoucherCategory.COST,
    createdAt: partial.date,
    caseVoucherId: crypto.randomUUID(),
    tags: [],
    appendix: null,
    currency: Currency.EUR,
    id: 0,
    label: '',
    state: null,
    caseId: null,
    createdBy: null,
    details: {},
    dueDate: null,
    importId: null,
    interest: null,
    interestEndDate: null,
    interestStartDate: null,
    lineItems: null,
    referenceCaseVoucherId: null,
    referenceId: null,
    setting: null,
    tenantId: '',
    text: null,
    transactionAccountId: null,
    expired: false,
    expirationManualSet: false,
    expirationDate: null,
    expirationFromDate: null,
    expirationLength: null,
    irrecoverableDate: null,
    cardType: null,
    paymentType: null,
    paymentMethodId: null,
    transactionImportId: null,
    deletedAt: null,
    deletedBy: null,
    exported: false,
    settingDetails: null,
    associationId: null,
});
export const genFakeDeposit = (amount, date) => ({
    amount,
    category: 'transaction',
    createdAt: date,
    date: date,
    caseVoucherId: '',
    tags: [],
    appendix: null,
    currency: Currency.EUR,
    voucherTypeId: VoucherTypeIds.DEPOSIT,
    // Sort after real vouchers at the same instant: an installment can only pay
    // debt that already exists.
    id: Number.MAX_SAFE_INTEGER,
    label: '',
    state: null,
    caseId: '',
    createdBy: null,
    details: {},
    dueDate: null,
    importId: null,
    interest: null,
    interestEndDate: null,
    interestStartDate: null,
    lineItems: null,
    referenceCaseVoucherId: null,
    referenceId: null,
    setting: VoucherTypeSetting.Payment,
    source: CaseVoucherSource.Collector,
    tenantId: '',
    text: null,
    transactionAccountId: null,
    expired: false,
    expirationManualSet: false,
    expirationDate: null,
    expirationFromDate: null,
    expirationLength: null,
    irrecoverableDate: null,
    cardType: null,
    paymentType: null,
    paymentMethodId: null,
    transactionImportId: null,
    deletedAt: null,
    deletedBy: null,
    exported: false,
    settingDetails: { interruptsLimitationPeriod: true },
    associationId: null,
});
const addInterestAmounts = async (caseVouchers, documents, economyPolicy, fixedDistributions, plan, conditions) => {
    // Settled entries are history: their payments are already real vouchers.
    const deposits = plan.map((p) => p.status === PaymentPlanEntryStatus.SETTLED
        ? null
        : genFakeDeposit(parseInt(`${p.amount}`), moment(p.date).format()));
    const futureVoucherDeposits = deposits.filter((d) => d != null);
    if (futureVoucherDeposits.length === 0) {
        return plan;
    }
    // Keyed by the day the engine reports as the breakpoint `to`: engine 1's
    // parseDate of the local-time deposit date, engine 2's tenant day.
    const dayOf = isEngineV2(economyPolicy)
        ? (date) => startOf(dayIn(economyPolicy.timezone)(date))
        : (date) => parseDate(date);
    const interestAmounts = new Map();
    if (caseVouchers.length === 0) {
        interestAmounts.set(dayOf(futureVoucherDeposits[0].date), 0);
    }
    let lastEntryDate = new Date(plan[plan.length - 1].date).getTime();
    for (const entry of plan) {
        const time = new Date(entry.date).getTime();
        if (lastEntryDate < time) {
            lastEntryDate = time;
        }
    }
    await helpers.interest.calculate(helpers.vouchers
        .postProcessing(caseVouchers, {
        includeDeletedVouchers: false,
        includeExpiredVouchers: false,
        includeIrrecoverableVouchers: false,
    })
        .concat(futureVoucherDeposits), documents, economyPolicy, fixedDistributions, moment(lastEntryDate).clone().format(), {
        appendHist: false,
        breakPointCallBack: (caseVouchers, _from, to) => {
            interestAmounts.set(to, helpers.overview.balanceCategory(caseVouchers, VoucherCategory.INTEREST));
        },
        ...conditions,
        freezeLimitation: true,
    });
    for (let i = 0; i < plan.length; i++) {
        const deposit = deposits[i];
        if (deposit == null) {
            continue;
        }
        const time = dayOf(deposit.date);
        const interestAmount = interestAmounts.get(time);
        if (interestAmount == null) {
            throw new Error(`Missing interest amount for date ${new Date(time).toISOString()}, when calculating interest amount`);
        }
        plan[i] = { ...plan[i], interestAmount };
    }
    return plan;
};
// Stable id for deterministic previews
const SIMULATED_INTEREST_FREE_PERIOD_ID = '6e1c3a52-9b7d-4f2e-8a44-1d2f3e4a5b6c';
// Injects the plan's interest-free window as a period for previews; skips
// vouchers already carrying an overlapping period.
export const interestFreePeriod = (caseVouchers, interestFree) => {
    const interestFreeWindow = {
        from: interestFree.from,
        to: interestFree.to ?? null,
    };
    const overlapsWindow = (period) => period.voidedAt === null &&
        interestPeriodWindowsOverlap(period, interestFreeWindow);
    return caseVouchers.map((caseVoucher) => {
        if (!(caseVoucher.setting &&
            CaseVoucherInterestSettings.includes(caseVoucher.setting))) {
            return caseVoucher;
        }
        if (caseVoucher.interestPeriods?.some(overlapsWindow)) {
            return caseVoucher;
        }
        return {
            ...caseVoucher,
            interestPeriods: (caseVoucher.interestPeriods ?? []).concat({
                id: SIMULATED_INTEREST_FREE_PERIOD_ID,
                from: interestFree.from,
                to: interestFree.to ?? null,
                rate: 0,
                onDefault: InterestPeriodDefaultBehavior.KeepFrozen,
                voidedAt: null,
                paymentPlanId: null,
                createdBy: null,
                createdAt: interestFree.from,
            }),
        };
    });
};
export const genAmortizationSchedule = async (plan, vouchers, economyPolicy, documents, fixedDistributions, conditions, feeStructure) => {
    // TODO: Use interest gen breakpoints for faster computations
    const schedule = [];
    let caseVouchers = JSON.parse(JSON.stringify(helpers.vouchers.postProcessing(vouchers, {
        includeDeletedVouchers: false,
        includeExpiredVouchers: false,
        includeIrrecoverableVouchers: false,
    })));
    for (const entry of plan) {
        if (entry.status === PaymentPlanEntryStatus.SETTLED) {
            const historicalVouchers = caseVouchers.filter((v) => moment(v.date) < moment(entry.cancel));
            const historicalVouchersWithInterst = await helpers.interest.calculate(historicalVouchers, documents, economyPolicy, fixedDistributions, moment(entry.date).format(), { ...conditions, freezeLimitation: true });
            const historicalTotal = -historicalVouchers
                .filter((v) => v.category !== VoucherCategory.TRANSACTION)
                .reduce((acc, v) => acc + v.amount, 0);
            const historicalInterest = -helpers.overview.balanceCategory(historicalVouchersWithInterst, VoucherCategory.INTEREST);
            const historicalPaid = helpers.overview.balanceCategory(historicalVouchersWithInterst, VoucherCategory.TRANSACTION);
            schedule.push({
                date: moment(entry.date).format('YYYY-MM-DD'),
                interest: historicalInterest,
                paid: historicalPaid,
                total: historicalTotal,
            });
            continue;
        }
        caseVouchers = caseVouchers.concat(genFakeDeposit(entry.amount || 0, moment(entry.date).format()));
        // Add mock per-entry fee for pending entries
        caseVouchers = appendPerEntryFeeVoucher(caseVouchers, feeStructure, moment(entry.date).format());
        const newVouchers = await helpers.interest.calculate(caseVouchers, documents, economyPolicy, fixedDistributions, moment(entry.date).format(), { ...conditions, freezeLimitation: true });
        const total = -newVouchers
            .filter((v) => v.category !== VoucherCategory.TRANSACTION)
            .reduce((acc, v) => acc + v.amount, 0);
        const interest = -helpers.overview.balanceCategory(newVouchers, VoucherCategory.INTEREST);
        const paid = helpers.overview.balanceCategory(newVouchers, VoucherCategory.TRANSACTION);
        schedule.push({
            date: moment(entry.date).format('YYYY-MM-DD'),
            interest,
            paid,
            total,
        });
    }
    return schedule;
};
//# sourceMappingURL=index.js.map